import { type Blocker } from 'react-router'
import { isEqual } from 'lodash'
import { nanoid } from 'nanoid'
import { AxiosError } from 'axios'
import { type IReactionDisposer, makeAutoObservable, reaction, runInAction } from 'mobx'
import { logger } from 'shared/lib'
import { errorHandler } from 'shared/api'
import { toastStore } from 'shared/ui'
import { uiStore } from 'shared/store/uiStore'
import {
  Chatbot,
  ChatbotApi,
  ChatbotErrors,
  ChatbotInstructionType,
  type IChatbotInstructionTemplate,
  type IChatbotFullData,
  type IChatbotPayload,
  type IAgentHandoffNotifySettings,
} from 'entities/Chatbot'
import type { IEntitiesStore } from 'entities/EntitiesExport'
import { ChatbotRoutes } from 'entities/ExportRouters'
import { TextingTemplates } from 'pages/chatbot/config'
import { StepObjectiveStore } from 'pages/chatbot/ui/ChatbotEdit/StepObjective'
import { BookingFieldsStore } from 'pages/chatbot/store/bookingFieldsStore'
import { StepQualifyStore } from 'pages/chatbot/ui/ChatbotEdit/StepQualify'
import { AgentKnowledgeBaseStore } from 'pages/chatbot/ui/AgentKnowledgeBase/store/AgentKnowledgeBaseStore'
import { StepHandoffStore } from 'pages/chatbot/ui/ChatbotEdit/StepHandoff'
import { ChatbotNumberId } from './chatbotNumberId'
import { ChatbotMessage } from './chatbotMessageStore'
import { ChatbotEnrollment } from './chatbotEnrollmentStore'
import { ChatbotFieldsControl } from './chatbotFieldsControlStore'
import { ChatbotAction } from './chatbotActionStore'
import { chatbotOperation } from './chatbotOperationStore'
import { AgentNavigationStore } from './agentNavigationStore'
import { AgentBookAppointmentStepsStore } from '../pages/agentBookAppointment/store/agentBookAppointmentStepsStore'

export class ChatbotStore {
  id: number | null = null
  model: Chatbot
  errors: ChatbotErrors
  numberId: ChatbotNumberId
  private readonly _message: ChatbotMessage
  enrollment: ChatbotEnrollment
  fieldsControl: ChatbotFieldsControl
  successActions: ChatbotAction
  fallbackActions: ChatbotAction
  qualifiedActions: ChatbotAction
  disqualifiedActions: ChatbotAction
  instructions = new Map<ChatbotInstructionType, IChatbotInstructionTemplate>()
  readonly agentKnowledgeBaseStore = new AgentKnowledgeBaseStore()
  private _agentNavigationStore = new AgentNavigationStore()
  private readonly _stepObjectiveStore: StepObjectiveStore
  private readonly _agentBookAppointmentStepsStore: AgentBookAppointmentStepsStore
  private readonly _bookingFieldsStore: BookingFieldsStore
  private readonly _stepQualifyStore: StepQualifyStore
  readonly stepHandoffStore: StepHandoffStore

  private _isAccessDenied = false
  private _isDragging = false
  private _initialized = false
  private _dirty = false
  private _originData: IChatbotPayload
  private _locked = false
  private _loaded = false
  private _disposeModelChange: IReactionDisposer | null = null
  private _disposeIsAircallReaction: IReactionDisposer | null = null
  isSaving = false
  private _validateTrigger = ''

  constructor(private _entities: IEntitiesStore) {
    this.model = new Chatbot({
      entities: this._entities,
    })
    this._bookingFieldsStore = new BookingFieldsStore(this._entities)
    this.errors = new ChatbotErrors(this.model)
    this.fieldsControl = new ChatbotFieldsControl(this.model)
    this.successActions = new ChatbotAction(
      this.model.successActions,
      this.errors.successActions,
      this._entities
    )
    this.fallbackActions = new ChatbotAction(
      this.model.fallbackActions,
      this.errors.fallbackActions,
      this._entities
    )
    this.qualifiedActions = new ChatbotAction(
      this.model.qualifiedActions,
      this.errors.qualifiedActions,
      this._entities
    )
    this.disqualifiedActions = new ChatbotAction(
      this.model.disqualifiedActions,
      this.errors.disqualifiedActions,
      this._entities
    )
    this._originData = this.model.toJSON()

    this.numberId = new ChatbotNumberId(this.model, this._entities)
    this.enrollment = new ChatbotEnrollment(this.model, this._entities)
    this._message = new ChatbotMessage(this.model, this.numberId.store.isAircall, this._entities)
    this._stepObjectiveStore = new StepObjectiveStore(this, this._entities)
    this._stepQualifyStore = new StepQualifyStore(this, this._entities)
    this.stepHandoffStore = new StepHandoffStore(this, this._entities)
    this._agentBookAppointmentStepsStore = new AgentBookAppointmentStepsStore(this, this._entities)

    this.reactionIsAircallInbox()
    this._detectDirty()

    makeAutoObservable(this)
  }

  get bookingFieldsStore() {
    return this._bookingFieldsStore
  }

  get errorObjective() {
    const errorDataCollectionInstruction = this.errors.getError(
      'settings.data_collection_instruction.value'
    )
    const errorMergeFieldsMinLimit = this.errors.getError(
      'settings.data_collection_instruction.merge_fields_min_1'
    )
    const errorMergeFieldsMaxLimit = this.errors.getError(
      'settings.data_collection_instruction.merge_fields_max_10'
    )
    const error = this.errors.getError('fields')
    const isOwnError = this.errors.hasOwnError('fields')
    const isNestedError = this.errors.hasError('fields')

    if (errorMergeFieldsMinLimit) return errorMergeFieldsMinLimit
    if (errorMergeFieldsMaxLimit) return errorMergeFieldsMaxLimit
    if (errorDataCollectionInstruction) return errorDataCollectionInstruction
    if (isOwnError && error) return error
    if (isNestedError) return 'Please complete all the fields'

    return
  }

  get errorObjectiveQualifyInstruction() {
    const errorQualifyInstruction = this.errors.getError('settings.qualify_instruction.value')

    if (errorQualifyInstruction) return errorQualifyInstruction

    return ''
  }

  get errorHandoff() {
    const errorHandoffEnabled = this.errors.getError('settings.handoff_enabled.value')

    if (errorHandoffEnabled) return errorHandoffEnabled

    const notifyKeys: Array<keyof IAgentHandoffNotifySettings> = [
      'notify_enabled',
      'conversation_assignee',
      'contact_owner',
      'all_inbox_members',
      'additional_users',
    ] as const

    let handoffSettingsError: string | null = null

    for (const key of notifyKeys) {
      handoffSettingsError = this.errors.getError(`settings.handoff_notify_settings.value.${key}`)
      if (handoffSettingsError) {
        break
      }
    }
    if (handoffSettingsError) {
      return handoffSettingsError
    }
    return this.stepHandoffStore.error
  }

  get message() {
    return this._message
  }

  get entities() {
    return this._entities
  }

  get dirty() {
    return this._dirty
  }

  get locked() {
    return this._locked
  }

  get loaded() {
    return this._loaded
  }

  get initialized() {
    return this._initialized
  }

  get isDraggingCondition() {
    return this._isDragging
  }

  get activeTemplates() {
    return TextingTemplates.filter((template) => this.instructions.has(template.type))
  }

  get stepObjectiveStore() {
    return this._stepObjectiveStore
  }

  get stepQualifyStore() {
    return this._stepQualifyStore
  }

  get agentNavigationStore() {
    return this._agentNavigationStore
  }

  get agentBookAppointmentStepsStore() {
    return this._agentBookAppointmentStepsStore
  }

  get isAccessDenied() {
    return this._isAccessDenied
  }

  get isDisabledSave() {
    if (this.id) {
      return !this.dirty || this.locked
    }
    return (
      !this.dirty ||
      (this.message.field.isAirCallNoSupportMms &&
        !this._entities.featureFlagsStore.aircall_new_api)
    )
  }

  get validateTrigger() {
    return this._validateTrigger
  }

  setValidateTrigger = () => {
    this._validateTrigger = nanoid()
  }

  setDraggingCondition = (condition: boolean) => {
    this._isDragging = condition
  }

  reactionIsAircallInbox = () => {
    this._disposeIsAircallReaction?.()
    this._disposeIsAircallReaction = reaction(
      () => this.numberId.store.isAircall,
      (isAircall) => {
        this.message.field.setIsCurrentAirCall(isAircall)
      }
    )
  }

  checkChanges = () => {
    const isBookAppointment = this.model.type === ChatbotInstructionType.BookAppointment

    return !isBookAppointment && this._dirty && !isEqual(this._originData, this.model.toJSON())
  }

  createChatbot: (confirmTest?: boolean) => Promise<number | null> = async (confirmTest) => {
    if (confirmTest) {
      const confirmed = await chatbotOperation.confirmTest(this.model.name, this.model.isActive)

      if (!confirmed) return null
    }

    const errorMinLimit = this.stepObjectiveStore.validateMinLimit(
      this.model.type !== ChatbotInstructionType.BookAppointment
    )
    const errorMaxLimit = this.stepObjectiveStore.validateMaxLimit()

    if (errorMinLimit) {
      this.setValidateTrigger()
      return null
    }
    if (errorMaxLimit) {
      this.setValidateTrigger()
      return null
    }

    const errorHandoff = this.stepHandoffStore.validate()
    if (errorHandoff) {
      this.setValidateTrigger()
      return null
    }

    this._locked = true

    try {
      const { data } = await chatbotOperation.create(this.model.toJSON())

      this.errors.clear()

      if (data.is_active) await chatbotOperation.enroll(data.id, data.name, data.filters)

      toastStore.add({
        title: 'AI Agent saved',
        type: 'info',
      })

      return runInAction(() => {
        this.id = data.id

        this._detectDirty()

        return data.id
      })
    } catch (error) {
      if (error instanceof AxiosError) {
        this.errors.sync(error.response?.data?.errors)
        this.setValidateTrigger()
      }

      logger.error(error)

      return null
    } finally {
      runInAction(() => {
        this._locked = false
      })
    }
  }

  confirmCreateChatbot = async (blocker: Blocker) => {
    this._locked = true

    try {
      await chatbotOperation.confirmCreate(this.model.toJSON())

      blocker.proceed?.()

      return true
    } catch (error) {
      if (error instanceof AxiosError) this.errors.sync(error.response?.data?.errors)

      blocker.reset?.()
      return false
    } finally {
      runInAction(() => {
        this._locked = false
      })
    }
  }

  checkValidateForPublish = () => {
    const errorMinLimit = this.stepObjectiveStore.validateMinLimit(
      this.model.type !== ChatbotInstructionType.BookAppointment
    )
    const errorMaxLimit = this.stepObjectiveStore.validateMaxLimit()

    if (this.model.settings.knowledgeBaseUsage) {
      const validateAgentKnowledgeBaseStore = this.agentKnowledgeBaseStore.checkValidate()
      if (!validateAgentKnowledgeBaseStore) return false
    }

    if (errorMinLimit) return false
    if (errorMaxLimit) return false

    const errorHandoff = this.stepHandoffStore.validate()
    return !errorHandoff
  }

  createAndPublishChatbot: (confirmTest?: boolean) => Promise<number | null> = async () => {
    this._locked = true

    const isValid = this.checkValidateForPublish()
    if (!isValid) {
      this.setValidateTrigger()
      return null
    }

    try {
      const { data } = await chatbotOperation.create({
        ...this.model.toJSON(),
        is_active: true,
      })

      this.errors.clear()

      if (data.is_active) await chatbotOperation.enroll(data.id, data.name, data.filters)

      return runInAction(() => {
        this.id = data.id

        this._detectDirty()

        return data.id
      })
    } catch (error) {
      if (error instanceof AxiosError) {
        this.errors.sync(error.response?.data?.errors)
        this.setValidateTrigger()
      }

      logger.error(error)

      return null
    } finally {
      runInAction(() => {
        this._locked = false
      })
    }
  }

  initResources = async () => {
    try {
      const {
        data: { templates },
      } = await ChatbotApi.getInstructions()

      return runInAction(async () => {
        templates.map((template) => this.instructions.set(template.type, template))

        this._agentBookAppointmentStepsStore.updateInstructions(this.instructions)

        return (this._initialized = true)
      })
    } catch (error) {
      logger.error(error)

      return runInAction(() => (this._initialized = false))
    }
  }

  loadChatbot = async (
    id: number,
    customSyncOrigin?: (data: IChatbotFullData) => void,
    initialData?: IChatbotFullData
  ) => {
    this._locked = true

    try {
      const { data } = initialData ? { data: initialData } : await chatbotOperation.load(id)

      runInAction(async () => {
        if (customSyncOrigin) customSyncOrigin(data)
        else {
          this.id = data.id
          this.model.syncOrigin(data)
        }

        if (this.model.type === ChatbotInstructionType.BookAppointment) {
          this.agentBookAppointmentStepsStore.init(undefined, data)
          this._loaded = true
        } else {
          await this.enrollment.syncFilters()
          if (!customSyncOrigin) {
            this._originData = this.model.toJSON()
            this._detectDirty()
          }
          this._loaded = true
        }
      })

      return true
    } catch (error) {
      errorHandler<void>(error as Error).then(({ type, error }) => {
        if (type === 'axios-error') {
          if (error.status === 403) {
            runInAction(() => {
              this._isAccessDenied = true
            })
          }
        }
      })

      logger.error(error)
      return false
    } finally {
      runInAction(() => {
        this._locked = false
      })
    }
  }

  publishChatbot = async (isPublished?: boolean) => {
    if (this.id === null) return false

    const isValid = this.checkValidateForPublish()
    if (!isValid) {
      this.setValidateTrigger()
      return null
    }

    try {
      const { data } = await chatbotOperation.update(this.id, {
        ...this.model.toJSON(),
        is_active: true,
      })

      this.errors.clear()

      if (data.is_active)
        await chatbotOperation.enroll(data.id, data.name, data.filters, isPublished)

      this._detectDirty()

      return true
    } catch (error) {
      if (error instanceof AxiosError) {
        this.errors.sync(error.response?.data?.errors)
        this.setValidateTrigger()
      }

      logger.error(error)
      return false
    }
  }

  updateChatbot = async (confirmTest?: boolean) => {
    if (this.id === null) return false

    const errorMinLimit = this.stepObjectiveStore.validateMinLimit(
      this.model.type !== ChatbotInstructionType.BookAppointment
    )
    const errorMaxLimit = this.stepObjectiveStore.validateMaxLimit()

    if (errorMaxLimit) {
      this.setValidateTrigger()
      return false
    }
    if (errorMinLimit) {
      this.setValidateTrigger()
      return false
    }

    const errorHandoff = this.stepHandoffStore.validate()
    if (errorHandoff) {
      this.setValidateTrigger()
      return false
    }

    if (confirmTest) {
      const confirmed = await chatbotOperation.confirmTest(this.model.name, this.model.isActive)

      if (!confirmed) return false
    }

    this._locked = true

    try {
      const payload = this.model.toJSON()
      await chatbotOperation.update(this.id, payload)

      this._originData = payload

      this.errors.clear()
      this._detectDirty()
      toastStore.add({
        title: 'Changes saved',
        type: 'info',
      })
      return true
    } catch (error) {
      if (error instanceof AxiosError) {
        this.errors.sync(error.response?.data?.errors)
        this.setValidateTrigger()
      }

      logger.error(error)
      return false
    } finally {
      runInAction(() => {
        this._locked = false
      })
    }
  }

  confirmUpdateChatbot = async (blocker: Blocker) => {
    if (this.id === null) return false

    this._locked = true

    try {
      const payload = this.model.toJSON()
      await chatbotOperation.confirmUpdate(this.id, payload)

      blocker.proceed?.()

      return true
    } catch (error) {
      if (error instanceof AxiosError) this.errors.sync(error.response?.data?.errors)

      blocker.reset?.()
      return false
    } finally {
      runInAction(() => {
        this._locked = false
      })
    }
  }

  disableChatbot = async () => {
    if (this.id === null) return false

    try {
      const { data } = (await chatbotOperation.updateStatus(this.id, false)) ?? {}
      runInAction(() => {
        if (!data) return

        this.model.syncOrigin(data)

        this._detectDirty()
      })
    } catch (error) {
      logger.error(error)
    }
  }

  setSaving = (isSaving: boolean) => {
    this.isSaving = isSaving
  }

  handleSaveAgent = (invalidate: () => void) => {
    this.setSaving(true)
    if (this.id) {
      this.updateChatbot().then((updated) => {
        this.setSaving(false)
        if (updated) invalidate()
      })
    } else {
      this.createChatbot().then((id) => {
        this.setSaving(false)
        if (id) uiStore.changeRoute({ path: `/${ChatbotRoutes.root}/${id}` })
      })
    }
  }

  handlePublishAgent = async () => {
    this.setSaving(true)

    const isPublished = this.id
      ? await this.publishChatbot(this.model.isActive)
      : await this.createAndPublishChatbot()
    this.setSaving(false)
    if (isPublished)
      uiStore.changeRoute({ path: `/${ChatbotRoutes.root}/${ChatbotRoutes.texting}` })
  }

  handleTestNewAgent = async () => {
    const id = await this.createChatbot(true)
    if (id !== null) {
      uiStore.changeRoute({
        path: `/${ChatbotRoutes.root}/${id}`,
        options: { state: { test: true } },
      })
    }
  }

  setLockedChatbot = (isLocked: boolean) => {
    this._locked = isLocked
  }

  dispose = () => {
    this.message.dispose()
    this.numberId.dispose()
    this.enrollment.dispose()
    this.fieldsControl.dispose()
    this.agentBookAppointmentStepsStore.dispose()
    this._disposeModelChange?.()
  }

  private _detectDirty = () => {
    this._disposeModelChange?.()
    this._dirty = false

    this._disposeModelChange = reaction(
      () => this.model.toJSON(),
      () => {
        this._disposeModelChange?.()
        this._dirty = true
      }
    )
  }
}
